import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import { NavLink, useParams } from "react-router-dom";
import userConstants from "../../shared/constants/user-constants";
import logo from "../LiveFeed/logo.png";
import "./RaceClock.scss";
import combinedServices from "../../shared/services/user-service";

const RaceClock = () => {
  const { id } = useParams();
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [heatNumber, setHeatNumber] = useState(null);
  const [eventTitle, setEventTitle] = useState("");
  const [eventDetail, setEventDetail] = useState(null);
  const [activeAudios, setActiveAudios] = useState([]);
  const [heatScores, setHeatScores] = useState(null);
  const [classId, setClassId] = useState(null);
  const [viewMode, setViewMode] = useState("clock");
  const [fetchScoreAgain, setFetchScoreAgain] = useState(true);

  const isRunningRef = useRef(isRunning);

  const eventDetailRef = useRef(eventDetail);
  const activeAudiosRef = useRef(activeAudios);

  // Memoize socket connection
  const socket = useMemo(() => io.connect(userConstants.baseURL), []);

  // Update refs whenever state changes
  useEffect(() => {
    eventDetailRef.current = eventDetail;
  }, [eventDetail]);

  useEffect(() => {
    activeAudiosRef.current = activeAudios;
  }, [activeAudios]);

  useEffect(() => {
    isRunningRef.current = isRunning;
  }, [isRunning]);

  // Fetch event details and active audios
  useEffect(() => {
    const fetchData = async () => {
      try {
        const eventResponse = await combinedServices.getEventDetail(id);
        setEventTitle(eventResponse.data.event_full_title);
        setEventDetail(eventResponse.data);
        const audiosResponse = await combinedServices.getActiveAudios();
        setActiveAudios(audiosResponse.data || []);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, [id]);

  // Fetch heat score when heatNumber, classId, or viewMode changes
  useEffect(() => {
    const fetchHeatScore = async () => {
      if (!heatNumber || !classId) return;
      try {
        const heatScoreResponse = await combinedServices.getHeatScore({
          eventId: id,
          classId: classId,
          heatNumber: heatNumber,
        });
        if (heatScoreResponse?.data?.participants) {
          const uniqueParticipants = Object.values(
            heatScoreResponse.data.participants.reduce((acc, p) => {
              acc[p.participantId] = p;
              return acc;
            }, {})
          );
          // Sort by score descending
          uniqueParticipants.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
          setHeatScores({
            ...heatScoreResponse.data,
            participants: uniqueParticipants,
          });
        }
      } catch (err) {
        console.error("Error fetching heat score:", err);
      }
    };

    // Only fetch heat scores if we're in participants mode or about to switch to it
    if (viewMode === "participants" || heatNumber) {
      fetchHeatScore();
    }
  }, [heatNumber, classId, id, viewMode, fetchScoreAgain]);

  // Socket event listeners
  useEffect(() => {

    if (id) {
      socket.emit("joinEvent", id);
    }

    const handleSyncRaceState = (state) => {

      console.log("Sync race state called", state);

      if (!state || String(state.eventId) !== String(id)) return;

      setHeatNumber(state.heatNumber);
      setClassId(state.classId);
      setViewMode(state.viewMode);

      if (state.viewMode === "clock" && state.isRunning) {
        setTime(state.timeRemaining);
        setIsRunning(state.timeRemaining > 0);
      } else {
        setIsRunning(false);
      }
    };

    const handleClockStart = (data) => {
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      if (parsed.eventId == id) {
        const { class_category, heat_number, class_id } = parsed;
        if (!eventDetailRef.current) return;
        if (heat_number) {
          setHeatNumber(heat_number);
          setClassId(class_id);
        }
        const audioName =
          class_category == 5
            ? eventDetailRef.current.event_electric_classes
            : eventDetailRef.current.event_non_electric_classes;
        const audio = activeAudiosRef.current.find((a) => a.name === audioName);
        if (audio) {
          // const seconds = convertToSeconds(audio.audio_len);
          // setTime(seconds);
          // Step 1: sync client clock to server
          const serverNow = parsed.updatedAt;
          const clientNow = Date.now();
          const clockDrift = clientNow - serverNow;

          const correctedNow = Date.now() - clockDrift;

          let offset = Math.round((parsed.updatedAt - parsed.startTime) / 1000);

          // Step 2: elapsed (ONLY THIS)
          const elapsed = Math.round(
            (correctedNow - parsed.startTime) / 1000
          );

          // Step 3: remaining
          const totalSeconds = convertToSeconds(audio.audio_len);

          const timeRemaining = Math.max(0, totalSeconds - elapsed + offset);

          console.log(
            "time remaining:",
            timeRemaining,
            "elapsed:",
            elapsed
          );
          setTime(timeRemaining);
          setIsRunning(true);
          setViewMode("clock");
        }
      }
    };

    const handleClockStop = (data) => {
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      if (parsed.eventId == id) {
        setTime(0);
        setIsRunning(false);
      }
    };

    const handleShowParticipants = (data) => {
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      if (parsed.eventId == id) {
        setViewMode("participants");
        setHeatNumber(parsed.heat_number);
        setClassId(parsed.class_id);
      }
    };

    const handleScoreUpdate = (data) => {
      console.log("🚀 ~ handleScoreUpdate ~ data:", data);
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      if (parsed[0].event_Id == id) {
        setFetchScoreAgain((prev) => !prev);
      }
    };

    const handleMillTimeStart = (data) => {
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      if (parsed.eventId == id) {
        const { class_category, heat_number, class_id } = parsed;
        if (!eventDetailRef.current) return;
        if (heat_number) {
          setHeatNumber(heat_number);
          setClassId(class_id);
        }
        setTime(30);
        setIsRunning(true);
        setViewMode("clock");
      }
    };

    socket.on("syncRaceState", handleSyncRaceState);
    socket.on("clockStart", handleClockStart);
    socket.on("clockStop", handleClockStop);
    socket.on("showParticipants", handleShowParticipants);
    socket.on("newScore", handleScoreUpdate);
    socket.on("millTimeStart", handleMillTimeStart);

    return () => {
      socket.off("syncRaceState", handleSyncRaceState);
      socket.off("clockStart", handleClockStart);
      socket.off("clockStop", handleClockStop);
      socket.off("showParticipants", handleShowParticipants);
      socket.off("newScore", handleScoreUpdate);
      socket.disconnect();
    };
  }, [id, socket]);

  // Countdown logic
  useEffect(() => {
    let interval;
    if (isRunning && time > 0) {
      interval = setInterval(() => {
        setTime((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }

    if (time === 0 && isRunning) {
      setIsRunning(false);
      // show 00:00 for 3s, then switch to participants
      setTimeout(() => {
        if(!isRunningRef.current)
          setViewMode("participants");
      }, 3000);
    }

    return () => clearInterval(interval);
  }, [isRunning, time]);

  // Helper functions
  const convertToSeconds = useCallback((len) => {
    const parts = len.split(":").map((p) => parseInt(p));
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    return 0;
  }, []);

  const formatTime = useCallback((seconds) => {
    // const hrs = Math.floor(seconds / 3600);
    // const mins = Math.floor((seconds % 3600) / 60);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    // return [hrs, mins, secs]
    //   .map((val) => val.toString().padStart(2, "0"))
    //   .join(":");
    return [mins, secs]
    .map((val) => val.toString().padStart(2, "0"))
    .join(":");
  }, []);

  // Render
  return (
    <div className="race-clock-page">
      <div className="newHeader">
        <div className="newLogo">
          <NavLink to="/">
            <img src={logo} alt="Logo" />
          </NavLink>
        </div>
        <div className="topHd">{eventTitle}</div>
      </div>

      {/* {heatNumber && (
        <div className="heat-info">
          Heat <span className="heat-number">{heatNumber}</span>
        </div>
      )} */}

      {/* <h1>Race Clock – Event {id}</h1> */}

      {viewMode === "participants" && heatScores && (
        <div className="heat-scores">
          {/* <h2>
            {heatScores.classname} – Heat {heatScores.heat_number} (Round{" "}
            {heatScores.round})
          </h2> */}
          <table className="scores-table">
            <thead>
              <tr>
                <th>Participant</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {heatScores.participants.map((p) => (
                <tr key={p.participantId}>
                  <td>{p.name}</td>
                  <td>{p.scratch ? "SCR" : p.score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewMode === "clock" && (
        <div className="clock-parent-div">
          <div
            className={`clock-display ${isRunning ? "running" : "stopped"} ${time <= 10 && isRunning ? "warning" : ""
              }`}
          >
            {formatTime(time)}
          </div>
        </div>
      )}
    </div>
  );
};

export default RaceClock;
